/**
 * Discounts: the doctor's standing night / first-visit discounts and their
 * dated promotional offers.
 *
 * Replaces `renderOffersManagement`, `openOfferModal`, `handleSaveOffer`,
 * `deleteOffer` (defined three times), `toggleOfferStatus`, `createBookingOffer`,
 * `saveBookingOffer`, `viewBookingOffers`, `toggleOffer` and the standing-discount
 * setters in app.js (`toggleSmartDiscount`, `updateNightDiscountPercent`,
 * `toggleFirstVisitDiscount`, `updateFirstVisitDiscount`).
 *
 * Standing discounts live on DoctorProfile and are saved through
 * PUT /api/doctors/profile. Dated offers are Offer rows with their own CRUD
 * routes under /api/doctors/offers.
 *
 * They did not have those routes until now: the Offer table was read-only, so
 * this screen kept edits in React state and they were gone on refresh. The
 * table also had no start date, which meant no offer could say *which* days it
 * covered — and so the offer badge on both calendars never appeared for anyone.
 * `startDate` was added alongside the write routes.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  SectionTitle,
  Spinner,
  DateInput,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { doctorApi } from '../../api/endpoints';
import { useAsync } from '../../hooks/useAsync';
import { normalizeDoctor } from '../../domain/catalog';
import { isOfferValid, normalizeOffer, offerPercent } from '../../domain/offers';
import {
  formatCurrency,
  calculateBookingPrice,
  resolveBasePrice,
  BOOKING_TYPES,
} from '../../domain/pricing';
import { isoToDisplayDate, todayKey } from '../../domain/dates';

const emptyOffer = () => ({
  id: null,
  name: '',
  percent: 10,
  // Defaults to today so a new offer is live immediately, which is what a
  // doctor creating one almost always means.
  startDate: todayKey(),
  expiry: '',
  active: true,
});

export function DoctorOffers() {
  const { doctorProfile, setDoctorProfile } = useAuth();
  const { success, error: toastError } = useNotifications();

  const [standing, setStanding] = useState({
    hasNightDiscount: false,
    nightDiscountPercent: 25,
    hasFirstVisitDiscount: false,
    firstVisitDiscountPercent: 10,
  });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!doctorProfile) return;
    setStanding({
      hasNightDiscount: Boolean(doctorProfile.hasNightDiscount),
      nightDiscountPercent: Number(doctorProfile.nightDiscountPercent) || 25,
      hasFirstVisitDiscount: Boolean(doctorProfile.hasFirstVisitDiscount),
      firstVisitDiscountPercent: Number(doctorProfile.firstVisitDiscountPercent) || 10,
    });
  }, [doctorProfile]);

  const offersState = useAsync(() => doctorApi.offers(), [], { initialData: [] });

  const offers = useMemo(
    () => (offersState.data || []).map(normalizeOffer),
    [offersState.data],
  );

  /**
   * Persist an offer, then refresh both this list and the doctor record.
   *
   * The doctor record carries `offers` too, and the calendars read them from
   * there — so a save that only updated this screen would leave the calendar
   * showing the previous state until the next full reload.
   */
  const persistOffer = async (form) => {
    const payload = {
      name: form.name?.trim(),
      percent: Number(form.percent),
      startDate: form.startDate || null,
      expiry: form.expiry || null,
      active: form.active !== false,
    };

    try {
      if (form.id) await doctorApi.updateOffer(form.id, payload);
      else await doctorApi.createOffer(payload);

      await offersState.reload();
      await refreshDoctorRecord();
      setEditing(null);
      success(form.id ? 'تم تحديث العرض' : 'تم إنشاء العرض');
    } catch (err) {
      toastError(err.message || 'تعذر حفظ العرض');
    }
  };

  const removeOffer = async (offer) => {
    try {
      await doctorApi.deleteOffer(offer.id);
      await offersState.reload();
      await refreshDoctorRecord();
      success('تم حذف العرض');
    } catch (err) {
      toastError(err.message || 'تعذر حذف العرض');
    } finally {
      setDeleteTarget(null);
    }
  };

  const refreshDoctorRecord = async () => {
    const fresh = await doctorApi.profile();
    setDoctorProfile(normalizeDoctor(fresh));
  };

  // "Running" means live today: not switched off, and not past its end date.
  // An offer that is merely switched off belongs with the inactive ones, not
  // with the running list where it would read as still discounting.
  const { active, expired } = useMemo(() => {
    const now = new Date();
    const running = (offer) => offer.active !== false && isOfferValid(offer, now);
    return {
      active: offers.filter(running),
      expired: offers.filter((offer) => !running(offer)),
    };
  }, [offers]);

  const saveStanding = async (next) => {
    setSaving(true);
    try {
      await doctorApi.updateProfile(next);
      // Re-read so the whole app sees the change (the booking quote reads these).
      const fresh = await doctorApi.profile();
      setDoctorProfile(normalizeDoctor(fresh));
      success('تم حفظ إعدادات الخصومات');
    } catch (err) {
      toastError(err.message || 'تعذر حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleStandingChange = (patch) => {
    const next = { ...standing, ...patch };
    setStanding(next);
  };

  /**
   * The doctor's real exam price, per clinic.
   *
   * This used to read `doctorProfile.price_exam` directly, which is the wrong
   * source: `resolveBasePrice` — the function the booking flow actually prices
   * with — takes the clinic's `examination_price` first and only falls back to
   * the profile. A doctor charging 256 at their clinic was shown 500 here,
   * because 500 is the DoctorProfile.priceExam column default that nobody had
   * ever overwritten. The worked example was doing its arithmetic on a number
   * no patient was ever quoted.
   *
   * Prices differ per clinic, so each one is listed rather than collapsed into
   * a single figure.
   */
  const clinicPrices = useMemo(() => {
    const clinics = doctorProfile?.clinics || [];
    if (clinics.length === 0) return [];
    return clinics.map((clinic) => ({
      id: clinic.id,
      name: clinic.name || 'العيادة',
      exam: resolveBasePrice({ doctor: doctorProfile, clinic, bookingType: BOOKING_TYPES.EXAM }),
      followup: resolveBasePrice({
        doctor: doctorProfile,
        clinic,
        bookingType: BOOKING_TYPES.FOLLOWUP,
      }),
    }));
  }, [doctorProfile]);

  /** The figure the offer preview quotes against — the first clinic's real price. */
  const examplePrice = clinicPrices[0]?.exam ?? null;

  if (!doctorProfile) return <Spinner label="جاري تحميل بيانات الطبيب..." />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="العروض والخصومات"
        subtitle="اضبط خصوماتك الدائمة وأنشئ عروضاً محددة المدة"
        icon="percent"
        actions={
          <Button icon="plus" onClick={() => setEditing(emptyOffer())}>
            عرض جديد
          </Button>
        }
      />

      {/* Standing discounts */}
      <Panel>
        <SectionTitle icon="settings-2">الخصومات الدائمة</SectionTitle>

        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-white/5 bg-white/5 p-4">
            <Checkbox
              label="خصم المواعيد المسائية"
              checked={standing.hasNightDiscount}
              onChange={(event) => handleStandingChange({ hasNightDiscount: event.target.checked })}
            />
            <p className="text-[11px] leading-relaxed text-slate-400">
              يُطبق تلقائياً على الحجوزات بعد الساعة 6 مساءً لتشجيع المرضى على المواعيد الأقل ازدحاماً.
            </p>
            <Field label="نسبة الخصم (%)">
              <Input
                type="number"
                min="0"
                max="100"
                disabled={!standing.hasNightDiscount}
                value={standing.nightDiscountPercent}
                onChange={(event) =>
                  handleStandingChange({ nightDiscountPercent: Number(event.target.value) })
                }
                className="sm:w-32"
              />
            </Field>
          </div>

          <div className="space-y-3 rounded-xl border border-white/5 bg-white/5 p-4">
            <Checkbox
              label="خصم الزيارة الأولى"
              checked={standing.hasFirstVisitDiscount}
              onChange={(event) =>
                handleStandingChange({ hasFirstVisitDiscount: event.target.checked })
              }
            />
            <p className="text-[11px] leading-relaxed text-slate-400">
              خصم ترحيبي للمرضى الجدد الذين يحجزون معك لأول مرة.
            </p>
            <Field label="نسبة الخصم (%)">
              <Input
                type="number"
                min="0"
                max="100"
                disabled={!standing.hasFirstVisitDiscount}
                value={standing.firstVisitDiscountPercent}
                onChange={(event) =>
                  handleStandingChange({ firstVisitDiscountPercent: Number(event.target.value) })
                }
                className="sm:w-32"
              />
            </Field>
          </div>
        </div>

        {/* Worked example, on the doctor's own prices. The legacy UI showed
            nothing here, and doctors consistently read cumulative discounts as
            additive — 25% then 10% off 256 is 172.80, not 216. */}
        {clinicPrices.length === 0 ? (
          <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
            <p className="flex items-start gap-2 text-[11px] text-amber-300">
              <Icon name="alert-circle" size={14} className="mt-px shrink-0" />
              لا توجد عيادة مسجلة، ولا يمكن عرض مثال على أسعارك الحقيقية. أضف عيادة وحدّد سعر
              الكشف من صفحة إدارة العيادات.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {clinicPrices.map((clinic) => {
              const night = standing.hasNightDiscount ? standing.nightDiscountPercent : 0;
              const first = standing.hasFirstVisitDiscount
                ? standing.firstVisitDiscountPercent
                : 0;

              return (
                <div
                  key={clinic.id}
                  className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="flex items-center gap-1.5 text-[11px] font-bold text-cyan-300">
                      <Icon name="building-2" size={13} />
                      {clinic.name}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      كشف {formatCurrency(clinic.exam)} · متابعة {formatCurrency(clinic.followup)}
                    </p>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    <Example label="بدون خصم" value={clinic.exam} />
                    <Example
                      label={night ? `مسائي (${night}%)` : 'مسائي (موقوف)'}
                      value={calculateBookingPrice(clinic.exam, { night }).finalPrice}
                      muted={!night}
                    />
                    <Example
                      label={
                        night || first
                          ? `مسائي + زيارة أولى${first ? ` (${first}%)` : ''}`
                          : 'مسائي + زيارة أولى (موقوف)'
                      }
                      value={calculateBookingPrice(clinic.exam, { night, day: first }).finalPrice}
                      muted={!night && !first}
                    />
                  </div>
                </div>
              );
            })}

            <p className="text-[10px] text-slate-500">
              الأرقام أعلاه هي أسعار الكشف الفعلية بعياداتك. الخصومات تُطبق بالتتابع: كل نسبة
              تُحسب على المتبقي بعد سابقتها، وليس على السعر الأصلي.
            </p>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button icon="save" loading={saving} onClick={() => saveStanding(standing)}>
            حفظ الخصومات الدائمة
          </Button>
        </div>
      </Panel>

      {/* Dated offers */}
      <Panel>
        <SectionTitle icon="tag">العروض المحددة المدة</SectionTitle>

        {offersState.loading && offers.length === 0 ? (
          <Spinner variant="inline" label="جاري تحميل العروض..." />
        ) : offers.length === 0 ? (
          <EmptyState
            icon="tag"
            title="لا توجد عروض"
            message="أنشئ عرضاً بنسبة خصم وتاريخ انتهاء لتظهر للمرضى عند الحجز."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyOffer())}>
                إنشاء عرض
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            {active.length > 0 && (
              <OfferGroup
                title="عروض سارية اليوم"
                offers={active}
                onEdit={setEditing}
                onDelete={setDeleteTarget}
              />
            )}
            {expired.length > 0 && (
              <OfferGroup
                title="عروض منتهية أو موقوفة"
                offers={expired}
                dimmed
                onEdit={setEditing}
                onDelete={setDeleteTarget}
              />
            )}
          </div>
        )}
      </Panel>

      <OfferModal
        offer={editing}
        examplePrice={examplePrice}
        exampleClinic={clinicPrices[0]?.name}
        onClose={() => setEditing(null)}
        onSave={persistOffer}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => removeOffer(deleteTarget)}
        destructive
        title="حذف العرض"
        confirmLabel="حذف"
        message={`سيتم حذف عرض "${deleteTarget?.name || ''}" نهائياً.`}
      />
    </div>
  );
}

function Example({ label, value, muted = false }) {
  return (
    <div className="rounded-lg bg-slate-900 p-2 text-center">
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className={`text-sm font-bold ${muted ? 'text-slate-500' : 'text-emerald-400'}`}>
        {formatCurrency(value)}
      </p>
    </div>
  );
}

function OfferGroup({ title, offers, dimmed = false, onEdit, onDelete }) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-bold text-slate-400">{title}</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={`flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3 ${
              dimmed ? 'opacity-60' : ''
            }`}
          >
            <span className="rounded-xl bg-emerald-500/10 p-2.5 text-emerald-400">
              <Icon name="tag" size={18} />
            </span>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{offer.name}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                  {offer.percent}% خصم
                </Badge>
                {offer.start && (
                  <Badge className="border-slate-600 bg-slate-800 text-slate-300">
                    من {isoToDisplayDate(offer.start) || offer.start}
                  </Badge>
                )}
                {offer.expiry && (
                  <Badge className="border-slate-600 bg-slate-800 text-slate-300">
                    ينتهي {isoToDisplayDate(offer.expiry) || offer.expiry}
                  </Badge>
                )}
                {offer.active === false && (
                  <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                    موقوف
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => onEdit(offer)}
                className="rounded-lg bg-white/5 p-2 text-slate-300 hover:bg-white/10"
                aria-label="تعديل"
              >
                <Icon name="pencil" size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(offer)}
                className="rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                aria-label="حذف"
              >
                <Icon name="trash-2" size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OfferModal({ offer, examplePrice, exampleClinic, onClose, onSave }) {
  const [form, setForm] = useState(offer);
  const [saving, setSaving] = useState(false);

  // `offer` arrives normalised for the list; the editor writes the raw column
  // names the API expects, so it reads through the normalised aliases once.
  useEffect(() => {
    if (!offer) return setForm(null);
    setForm({
      id: offer.id ?? null,
      name: offer.name || '',
      percent: offerPercent(offer) || '',
      startDate: (offer.startDate || offer.start || '').slice(0, 10),
      expiry: (offer.expiry || offer.end || '').slice(0, 10),
      active: offer.active !== false,
    });
  }, [offer]);

  if (!form) return null;

  const percent = offerPercent(form);
  // No clinic means no real price to quote against; showing 0 ج.م — or a made-up
  // round number — would be worse than showing nothing.
  const preview = examplePrice ? calculateBookingPrice(examplePrice, { day: percent }) : null;
  const dateProblem =
    form.startDate && form.expiry && form.startDate > form.expiry
      ? 'تاريخ الانتهاء قبل تاريخ البداية'
      : '';

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل العرض' : 'عرض جديد'}
      icon="tag"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button
            icon="save"
            loading={saving}
            disabled={!form.name?.trim() || !percent || dateProblem}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(form);
              } finally {
                setSaving(false);
              }
            }}
          >
            حفظ
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="اسم العرض" required>
          <Input
            value={form.name || ''}
            onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
            placeholder="مثال: عرض رمضان"
          />
        </Field>

        <Field label="نسبة الخصم (%)" required>
          <Input
            type="number"
            min="1"
            max="100"
            value={form.percent ?? ''}
            onChange={(event) => setForm((c) => ({ ...c, percent: Number(event.target.value) }))}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="تاريخ البداية" hint="اتركه فارغاً ليبدأ فوراً">
            <DateInput
              value={form.startDate || ''}
              onChange={(event) => setForm((c) => ({ ...c, startDate: event.target.value }))}
            />
          </Field>

          <Field
            label="تاريخ الانتهاء"
            hint="اتركه فارغاً لعرض دائم"
            error={dateProblem || undefined}
          >
            <DateInput
              min={form.startDate || undefined}
              value={form.expiry || ''}
              onChange={(event) => setForm((c) => ({ ...c, expiry: event.target.value }))}
            />
          </Field>
        </div>

        <Checkbox
          label="العرض مفعّل"
          checked={form.active !== false}
          onChange={(event) => setForm((c) => ({ ...c, active: event.target.checked }))}
        />

        {/* The days this offer covers are the days it marks on both calendars,
            so the range is stated back rather than left to be inferred. */}
        <p className="text-[11px] text-slate-500">
          {form.startDate && form.expiry
            ? `يظهر العرض على أيام التقويم من ${form.startDate} إلى ${form.expiry}.`
            : form.expiry
              ? `يظهر العرض على كل الأيام حتى ${form.expiry}.`
              : form.startDate
                ? `يظهر العرض على يوم ${form.startDate} فقط — أضف تاريخ انتهاء لمدة أطول.`
                : 'أضف تاريخ بداية أو انتهاء حتى يظهر العرض على التقويم.'}
        </p>

        {percent > 0 && preview && (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-center">
            <p className="text-[11px] text-slate-400">
              سعر الكشف بعد الخصم{exampleClinic ? ` — ${exampleClinic}` : ''} (من{' '}
              {formatCurrency(examplePrice)})
            </p>
            <p className="text-lg font-bold text-emerald-400">
              {formatCurrency(preview.finalPrice)}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default DoctorOffers;
